import groq from 'groq';

import { getLanguageSuffixByMarket } from '../../data/sanity/queries';
import {
  contentDefinedByLocale,
  contentFilterByLocale,
  getImageQueryProperties,
} from '../../data/sanity/queryFragments';
import { SemboMarket } from '../../utils/markets';

export const linksByMarket = (market: SemboMarket) => {
  return groq`
      *[_type == "market" && siteDomain == "${market.host}"][0].links[]-> {
        name,
        url
      }
    `;
};

export const firstMarketTripCollection = (market: SemboMarket) => {
  const language = getLanguageSuffixByMarket(market);

  return groq`
    *[
      _type == "market" &&
      siteDomain == "${market.host}"
    ][0].tripCollections[0]-> {
      _id,
      "name": name${language},
      "tripIds": trips[${contentDefinedByLocale(market, { isReference: true })}]._ref
    }
  `;
};

export const campaignsAndTeasersByMarket = (market: SemboMarket) => {
  return groq`
      *[_type == "market" && siteDomain == "${market.host}"][0].campaigns[
        ${contentDefinedByLocale(market, { isReference: true })} && @->image != null
      ]
    `;
};

export const marketImageByMarketHost = (
  market: SemboMarket,
  mediaName: 'image' | 'expressiveImage'
) => {
  return groq`
      *[_type == "market" && siteDomain == "${market.host}"][0]["${mediaName}"]-> {
        ${getImageQueryProperties(market)}
        }
    `;
};

export const mainBannerByMarket = (market: SemboMarket) => {
  return groq`
    *[_type == "market" && siteDomain == "${market.host}"][0].mainBanner -> {
      "_id": _id,
      "_type": _type,
      "media": coalesce(
        ${contentFilterByLocale(market)}.video -> {
          url,
          _type
        },
        video -> {
          url,
          _type
        },
        image -> {
          ${getImageQueryProperties(market)}
        },
      ),
      ...select(
        _type == 'campaign' => {
          ...${contentFilterByLocale(market)} {
            salesPitch,
            headline,
            "slug": slug.current
          }
        },
        _type == 'article' => {
          ...(${contentFilterByLocale(market)} {
        headline,
        "articleVideo": coalesce(
          ${contentFilterByLocale(market)}.video -> {
            url,
           _type
          },
          video -> {
            url,
            _type
          },
      ),
        "slug": slug.current,
        description[]{
          ...,
          _type == "media" => {
            "image": {
              ${getImageQueryProperties(market)}
            }
          },
          _type == "mediaWrapper" => {
            "image": {
              ${getImageQueryProperties(market, 'media->')},
              "caption": caption,
            }
          },
          markDefs[]{
            ...,
            _type == "link" => {
              "content": ${contentFilterByLocale(market, { isReference: '@.reference->' })} {
                _type,
                slug
              },
              "username": @.reference->username
            },
          }
        }
      }),
        }
      )
  }
  `;
};

export const secondaryCampaignByMarket = (market: SemboMarket) => {
  return groq`
    *[_type == "market" && siteDomain == "${market.host}"][0].secondaryCampaign -> {
      "_id": _id,
      "media": cardImage -> {
        ${getImageQueryProperties(market)}
      },
    ...${contentFilterByLocale(market)} {
      salesPitch,
      headline,
      "slug": slug.current
    },
  }
  `;
};

export const aiSuggestionsByMarket = (market: SemboMarket) => {
  const language = getLanguageSuffixByMarket(market);

  return groq`
    *[_type == "market" && siteDomain == "${market.host}"][0].aiSuggestions[@-> name${language} != null && length(@->name${language}) > 9] ->
        name${language}
  `;
};

export const tagsWithTripIds = (market: SemboMarket) => {
  return groq`
    *[_type == "market" && siteDomain == "${market.host}"][0] {
      "tags": coalesce(tagCollections[0]-> tags[${contentDefinedByLocale(market, { isReference: true })}] -> {
        _id,
        ...${contentFilterByLocale(market)} {
          "slug": slug.current,
          name,
        },
        "tripIds": *[
          _type == "trip" &&
          (!defined(unlisted) || unlisted == false) &&
          author == null &&
          ^._id in tags[]._ref &&
          ${contentDefinedByLocale(market)}
        ]._id
      }, [])
    }.tags[count(tripIds) > 0]
  `;
};

export const tagsByMarket = (market: SemboMarket) => {
  return groq`
    *[_type == "market" && siteDomain == "${market.host}"][0] {
      "tags": coalesce(tagCollections[0]-> tags[${contentDefinedByLocale(market, { isReference: true })}] -> {
        _id,
        ...${contentFilterByLocale(market)} {
          "slug": slug.current,
          name,
        },
      }, [])
    }.tags[]
  `;
};
